import { Maybe } from 'graphql/jsutils/Maybe'
import { NextPage } from 'next'
import { paths } from '@/constants/paths'
import { PageSeo } from '@/page'
import { getPageByPath } from '@/services/CmsService/Pages'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { WatchlistProvider } from '@/services/WatchlistService/WatchlistProvider'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { determinePageRevalidation } from '@/utils/nextUtils/revalidation'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { DiscoverPageView } from '@/views/WatchProjectView'

interface DiscoverPageProps {
  locale: string
  pageDataContext: Maybe<PageDataContext>
  page: PageModel
  discoverySlug: string
}

export const WatchProjectPage: NextPage<DiscoverPageProps> = (props) => {
  return (
    <>
      <PageSeo page={props.page} locale={props.locale} />
      <WatchlistProvider>
        <DiscoverPageView {...props} />
      </WatchlistProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<DiscoverPageProps>(
  { filePath: 'pages/[locale]/discover/[pageSlug]/index.tsx' },
  async ({ locale, federationClient, preview, next404, params }) => {
    const discoverySlug = getItemOrFirstEntry(params.pageSlug)

    if (!discoverySlug) return next404()

    const [page, translations] = await Promise.all([
      getPageByPath(`${paths.discover.index}/${discoverySlug}`, { preview, locale }),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'guild',
        'theatrical-presales',
      ]),
    ])

    if (!page)
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }

    const pageDataContext = await fetchPageDataContext(page, preview, locale, federationClient)

    return {
      props: {
        locale,
        discoverySlug: discoverySlug ?? '',
        page,
        pageDataContext,
        ...translations,
      },
      revalidate: determinePageRevalidation(page, pageDataContext),
    }
  },
)

export const getStaticPaths = noPreRenderedStaticPaths

export default WatchProjectPage
